<template>
    <div class="flex flex-col">
        <BasicForm
            class="flex flex-col gap-[30px]"
            @submit.prevent="submit"
        >
            <BasicInput
                v-bind="emailAttrs"
                v-model="email"
                :error-message="translatedErrors.email"
                :label="translations.email"
                name="email"
                type="email"
                data-testid="email-input-login-form"
            ></BasicInput>
            <BasicInput
                v-bind="passwordAttrs"
                v-model="password"
                :error-message="translatedErrors.password"
                :label="translations.password"
                type="password"
                name="password"
                data-testid="password-input-login-form"
            ></BasicInput>
            <BasicButton
                id="submit-login-button"
                :disabled="isSubmitting"
                :is-loading="isLoading"
                supervise-hydration
                type="submit"
                data-testid="submit-button-login-form"
            >
                {{ translations.submit }}
            </BasicButton>
        </BasicForm>
        <BasicNuxtLink
            id="go-to-reset-password-button"
            href="/reset-password"
            class="mt-[8px]"
            data-testid="reset-password-link-login-form"
        >
            {{ translations.resetPasswordButton }}
        </BasicNuxtLink>
    </div>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';

import BasicButton from '@atoms/BasicButton/BasicButton.vue';
import BasicForm from '@atoms/BasicForm/BasicForm.vue';
import BasicInput from '@atoms/BasicInput/BasicInput.vue';
import BasicNuxtLink from '@atoms/BasicNuxtLink.vue';

import { useFetchErrors } from '@composables/useFetchErrors';
import { useTranslatedErrors } from '@composables/useTranslatedErrors';

import { object, string } from '@services/yup';

import { useAuthStore } from '@stores/auth';

import type {
    ApiFetchCommonError,
    LoginErrorData,
    LoginPayload,
} from '@repository/types';

const NOT_VERIFIED_ERROR = 'atp.error.user.not_verified';

const authStore = useAuthStore();
const { t } = useI18n();
const { query } = useRoute();
const { errorLoggerSetUser } = useErrorLogger();

const validationSchema = object().shape({
    email: string().required().isEmailValid(),
    password: string().required(),
});

const { handleSubmit, errors, defineField, isSubmitting } = useForm({
    validationSchema,
});
const { showErrors } = useFetchErrors();
const { translatedErrors } = useTranslatedErrors(errors);

const [email, emailAttrs] = defineField('email');
const [password, passwordAttrs] = defineField('password');

const isNotVerified = (errors: string[]) => {
    return errors.includes(NOT_VERIFIED_ERROR);
};

const isLoading = ref(false);

const eventUrlCookie = useCookie<string | null>(CookiesNames.EVENT_URL);

const startChatCookie = useCookie<string | null>(CookiesNames.START_CHAT_WITH);

const { $api, $analyticsSetUserId } = useNuxtApp();

const handleEventRedirect = (eventRedirectUrl: string) => {
    try {
        const decodedEventUrl = atob(eventRedirectUrl);
        eventUrlCookie.value = null;
        return navigateTo(decodedEventUrl, {
            external: true,
        });
    } catch (error) {
        return navigateTo({ name: 'index', query });
    }
};

const handleChatRedirect = async (hostId: string) => {
    try {
        const { session_id: sessionId } =
            await $api.chat.getChatSessionId(hostId);
        await navigateTo(`/chats/${sessionId}`);
        startChatCookie.value = null;
    } catch (error) {
        if ((error as ApiFetchCommonError).statusCode === 404) {
            startChatCookie.value = null;
        }
        return navigateTo({ name: 'index', query });
    }
};

const submit = handleSubmit(async (values) => {
    isLoading.value = true;
    try {
        const { user, id, notifyAboutUpcomingTosVersion } =
            await $api.auth.login(values as LoginPayload);
        authStore.user = { id, email: user, notifyAboutUpcomingTosVersion };
        $analyticsSetUserId(id);
        errorLoggerSetUser({ id });

        await authStore.getUserDetails();

        const eventRedirectUrl =
            getFirstIfArray(query.event_url) || eventUrlCookie.value;

        if (eventRedirectUrl) {
            return handleEventRedirect(eventRedirectUrl);
        }

        if (startChatCookie.value) {
            return await handleChatRedirect(startChatCookie.value);
        }

        return navigateTo({ name: 'index', query });
    } catch (error) {
        const errorData = (error as LoginErrorData).data?.data;
        if (
            isNotVerified(errorData?.errors?.common || []) &&
            errorData?.email &&
            errorData?.id
        ) {
            authStore.user = { email: errorData.email, id: errorData.id };
            authStore.showEmailConfirmationPage = true;
            return navigateTo('/email-confirmation');
        }
        if (errorData?.errors) showErrors(errorData.errors);
        isLoading.value = false;
    }
});

const translations = computed(() => ({
    email: t('atp.components.forms.login.email'),
    password: t('atp.components.forms.login.password'),
    submit: t('atp.components.forms.login.submit'),
    resetPasswordButton: t('atp.components.forms.login.reset_password'),
}));
</script>
